/**
 * The checkout's credit lot picker: one row per lot the customer holds, a
 * checkbox on the ones that can pay this order, and the reason on the ones
 * that cannot.
 *
 * `GET /credit/balance?orderId=…` lists the lots (`lots`), already judged by
 * the server: `applicable` and `reason` are its answer, never re-derived
 * here. `selected` is what the order spends by — the customer's own choice
 * (`selectionMode` MANUAL), or the automatic one (soonest-expiring first).
 * A tap sends the whole new choice as `lotIds` to apply-credit.
 *
 * Pure, so it is tested; `CreditSection` only renders it.
 */
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { APEX_HOST_BY_TLD } from "./domain.util";
import { DOMAIN_MAP_TO_CURRENCY } from "@configs/currencies";

export type CreditLotReason =
  | "MINIMUM_ORDER"
  | "OTHER_MARKET"
  | "OTHER_CURRENCY"
  | "HELD_ON_OTHER_CART";

/** One lot as GET /credit/balance lists it (`CreditLotViewDto`). */
export type CreditLotView = {
  id: string;
  kind: "GIFT_CARD" | "GRANTED";
  codeHint: string | null;
  availableCents: number;
  currency: string;
  expiresAt: string | null;
  minimumOrderCents: number;
  market: string | null;
  applicable: boolean;
  reason: CreditLotReason | null;
  missingCents: number | null;
  selected: boolean;
  appliedCents: number;
};

export type Message = { key: string; fallback: string; params: Record<string, string> };

/**
 * Which credit box the delivery step shows:
 *
 *  - "picker": the server listed the customer's lots and the order can
 *    still change — one row per lot;
 *  - "none": it listed none and nothing is applied — no credit, so no box
 *    and no heading at all (the gift-card code field is separate);
 *  - "legacy": the single "use my credit" box, kept for every case the
 *    picker cannot answer: the lots are not known (loading, a failed read,
 *    an older server), the order is placed or its payment has begun (a
 *    read-only amount and, when paying, why it no longer moves), or the
 *    account is unconfirmed (the notice instead of a control).
 */
export function creditLotMode(input: {
  orderEditable: boolean;
  accountUnverified: boolean;
  lots: readonly CreditLotView[] | null;
  hasCreditApplied: boolean;
}): "picker" | "none" | "legacy" {
  if (!input.orderEditable || input.accountUnverified || input.lots === null) {
    return "legacy";
  }
  if (input.lots.length === 0 && !input.hasCreditApplied) return "none";
  return "picker";
}

/**
 * The `lotIds` a tap on `lotId` sends: what is chosen now, with that lot
 * flipped. A chosen lot that cannot pay yet (below its minimum) stays in:
 * the server keeps it chosen and spends it once the cart grows. [] is
 * "none", which takes the credit off.
 */
export function nextLotSelection(
  lots: readonly CreditLotView[],
  lotId: string,
): string[] {
  const chosen = lots.filter((l) => l.selected).map((l) => l.id);
  if (!lots.some((l) => l.id === lotId)) return chosen;
  return chosen.includes(lotId)
    ? chosen.filter((id) => id !== lotId)
    : lots.filter((l) => l.selected || l.id === lotId).map((l) => l.id);
}

/**
 * How one row renders. A lot that cannot pay and is not chosen is never a
 * checkbox. One the customer CHOSE that has since stopped applying (the cart
 * fell below its minimum) stays ticked — it is still chosen and comes back
 * when the cart grows — and can be unticked, paying nothing meanwhile.
 * `pending` is a tap still on its way to the server: its row shows where it
 * is going.
 */
export function lotRowState(
  lot: CreditLotView,
  pending?: { lotId: string; checked: boolean } | null,
): { selectable: boolean; checked: boolean; appliedCents: number } {
  if (!lot.applicable) {
    if (!lot.selected) return { selectable: false, checked: false, appliedCents: 0 };
    const checked = pending && pending.lotId === lot.id ? pending.checked : true;
    return { selectable: true, checked, appliedCents: 0 };
  }
  const checked = pending && pending.lotId === lot.id ? pending.checked : lot.selected;
  return { selectable: true, checked, appliedCents: lot.appliedCents };
}

/** "Darilni bon ···7K2P" or "Dobroimetje". */
export function lotLabelMessage(lot: CreditLotView): Message {
  if (lot.kind === "GIFT_CARD") {
    return lot.codeHint
      ? { key: "credit.lotGiftCard", fallback: "Darilni bon ···{{hint}}", params: { hint: lot.codeHint } }
      : { key: "credit.lotGiftCardPlain", fallback: "Darilni bon", params: {} };
  }
  return { key: "credit.lotGranted", fallback: "Dobroimetje", params: {} };
}

/**
 * "velja do 23. 9. 2027". A gift card never expires (it is money someone
 * paid, by design), and says so rather than showing nothing.
 */
export function lotExpiryMessage(
  lot: Pick<CreditLotView, "expiresAt"> & { kind?: CreditLotView["kind"] },
  formatDate: (date: Date) => string,
): Message | null {
  if (!lot.expiresAt) {
    return lot.kind === "GIFT_CARD"
      ? { key: "credit.lotNoExpiry", fallback: "brez roka veljavnosti", params: {} }
      : null;
  }
  return {
    key: "credit.lotExpires",
    fallback: "velja do {{date}}",
    params: { date: formatDate(new Date(lot.expiresAt)) },
  };
}

/** The shop a market's gift card is spent on, as a customer knows it. */
export function marketDomain(market: string | null | undefined): string | null {
  if (!market) return null;
  return APEX_HOST_BY_TLD[market] ?? null;
}

/**
 * The one shop a currency is spent in, from the domain/currency config:
 * PLN → foto-chwila.pl. Null when several shops take it (EUR) or none of
 * ours has a site to name (RON): naming one would be a guess.
 */
export function currencyDomain(currency: string | null | undefined): string | null {
  const code = (currency || "").toLowerCase();
  if (!code) return null;
  const tlds = Object.entries(DOMAIN_MAP_TO_CURRENCY)
    .filter(([, c]) => c === code)
    .map(([tld]) => tld);
  return tlds.length === 1 ? (APEX_HOST_BY_TLD[tlds[0]] ?? null) : null;
}

/** Why a lot cannot pay this order, in one short line; null when it can. */
export function lotReasonMessage(
  lot: CreditLotView,
  money: (cents: number) => string,
  domainOf: (market: string) => string | null,
): Message | null {
  if (lot.applicable) return null;
  switch (lot.reason) {
    case "MINIMUM_ORDER": {
      const minimum = money(lot.minimumOrderCents);
      return lot.missingCents !== null && lot.missingCents > 0
        ? {
            key: "credit.lotReasonMinimumMissing",
            fallback: "Za naročila od {{minimum}} naprej. Dodajte še za {{missing}} in se odšteje.",
            params: { minimum, missing: money(lot.missingCents) },
          }
        : {
            key: "credit.lotReasonMinimum",
            fallback: "Za naročila od {{minimum}} naprej (poštnina se ne šteje)",
            params: { minimum },
          };
    }
    case "OTHER_MARKET": {
      const domain = lot.market ? domainOf(lot.market) : null;
      return domain
        ? { key: "credit.lotReasonMarket", fallback: "Velja na {{domain}}", params: { domain } }
        : {
            key: "credit.lotReasonMarketUnknown",
            fallback: "Velja v drugi naši spletni trgovini",
            params: {},
          };
    }
    case "OTHER_CURRENCY": {
      const currency = lot.currency.toUpperCase();
      // Where it CAN be spent: the lot's own market when it has one, else
      // the one shop its currency belongs to.
      const domain = (lot.market ? domainOf(lot.market) : null) ?? currencyDomain(lot.currency);
      return domain
        ? {
            key: "credit.lotReasonCurrencyShop",
            fallback: "Velja za nakupe v valuti {{currency}}. Porabite ga lahko v trgovini {{domain}}.",
            params: { currency, domain },
          }
        : {
            key: "credit.lotReasonCurrency",
            fallback: "Velja za nakupe v valuti {{currency}}",
            params: { currency },
          };
    }
    case "HELD_ON_OTHER_CART":
      return {
        key: "credit.lotReasonHeld",
        fallback: "Uporabljeno pri drugem naročilu, ki še ni plačano",
        params: {},
      };
    default:
      return {
        key: "credit.lotReasonOther",
        fallback: "Pri tem naročilu ga ni mogoče uporabiti",
        params: {},
      };
  }
}

/**
 * One "paid by" line on a confirm page, in minor units, positive.
 *
 *  - `credit`: GRANTED credit; `giftCard`: a gift card;
 *  - `prepaid`: one or the other, not known which (the lots could not be
 *    read), or both when they cannot be split honestly. Labelled neutrally.
 *
 * `cents` null: the order owes nothing and this line pays the rest. No
 * figure is printed then, because the stored credit can exceed what the
 * order cost.
 */
export type CreditPaymentLine = {
  kind: "credit" | "giftCard" | "prepaid";
  cents: number | null;
};

/**
 * The credit an order uses, as lines for a page that lists the order's
 * prices (the COD confirm page), so those lines add up to `order.amount`.
 *
 * Split the way the backend's snapshot does (`buildLineItemsSnapshot`): the
 * gift-card part is what the GIFT_CARD lots put on this order, never more
 * than the order's applied total, and the rest is GRANTED credit. The lots
 * only say how it divides; not known (no credential to ask with, a failed
 * read), one `prepaid` line, since a gift card must not be called credit.
 *
 * THE CLAMP. The server subtracts `min(creditAppliedCents, preCreditTotal)`
 * (`effectiveCreditCents`), and the stored figure can exceed the total when
 * the cart shrank before the ledger released the excess. The browser does
 * not know the pre-credit total, but it does not need to:
 *
 *   amount = preCredit - min(applied, preCredit)
 *
 * so `amountCents > 0` proves `applied < preCredit`, and the stored figure
 * is exactly what was subtracted. At `amountCents === 0` the credit paid
 * everything listed above it and may be larger than that, so the line pays
 * "the rest" with no figure, and a split that cannot be clamped becomes one
 * `prepaid` line.
 */
export function creditPaymentLines(
  appliedCents: number,
  lots: readonly CreditLotView[] | null,
  order: { amountCents: number },
): CreditPaymentLine[] {
  const total = Math.max(0, Math.round(appliedCents) || 0);
  if (total === 0) return [];
  const coversAll = !(Math.round(order.amountCents) > 0);
  if (lots === null) return [{ kind: "prepaid", cents: coversAll ? null : total }];
  const cardOnLots = lots
    .filter((l) => l.kind === "GIFT_CARD")
    .reduce((sum, l) => sum + Math.max(0, l.appliedCents || 0), 0);
  const giftCard = Math.min(cardOnLots, total);
  const credit = total - giftCard;
  if (coversAll) {
    if (credit > 0 && giftCard > 0) return [{ kind: "prepaid", cents: null }];
    return [{ kind: giftCard > 0 ? "giftCard" : "credit", cents: null }];
  }
  const lines: CreditPaymentLine[] = [];
  if (credit > 0) lines.push({ kind: "credit", cents: credit });
  if (giftCard > 0) lines.push({ kind: "giftCard", cents: giftCard });
  return lines;
}

/**
 * A lot on the account page (GET /credit/mine), where there is no order to
 * judge it against: the conditions it comes with, in the checkout's words —
 * its minimum, the shop a gift card is spent on — then until when it is
 * valid.
 */
export function accountLotDetails(
  lot: {
    kind?: "GIFT_CARD" | "GRANTED";
    codeHint?: string | null;
    currency: string;
    expiresAt: string | null;
    minimumOrderCents: number;
    market: string | null;
  },
  money: (cents: number) => string,
  formatDate: (date: Date) => string,
  domainOf: (market: string) => string | null,
): Message[] {
  const out: Message[] = [];
  if (lot.minimumOrderCents > 0) {
    out.push({
      key: "credit.lotReasonMinimum",
      fallback: "Za naročila od {{minimum}} naprej (poštnina se ne šteje)",
      params: { minimum: money(lot.minimumOrderCents) },
    });
  }
  const domain = lot.market ? domainOf(lot.market) : null;
  if (domain) {
    out.push({ key: "credit.lotReasonMarket", fallback: "Velja na {{domain}}", params: { domain } });
  }
  const expiry = lotExpiryMessage(lot, formatDate);
  if (expiry) out.push(expiry);
  return out;
}

/**
 * The `lotIds` to apply with right after a gift-card code was redeemed.
 *
 * Typing a code is the request to use that card. In automatic mode that is
 * simply an apply without lotIds (undefined). With a manual choice, an apply
 * without lotIds would throw the choice away, so it is kept and the new card
 * — a lot the list did not have before the code (`knownBefore`) — is added.
 */
export function selectionAfterRedeem(
  mode: "AUTO" | "MANUAL" | undefined,
  knownBefore: readonly string[],
  lotsAfter: readonly CreditLotView[],
): string[] | undefined {
  if (mode !== "MANUAL") return undefined;
  const before = new Set(knownBefore);
  return lotsAfter.filter((l) => l.selected || !before.has(l.id)).map((l) => l.id);
}

/**
 * Offer a plain "take the credit off" when credit is on the order and no
 * ticked ticket is left to untick: a lot scope that lists none of the lots
 * paying, or only lots that no longer apply and were never chosen.
 */
export function showRemoveCreditAction(
  lots: readonly CreditLotView[],
  appliedCents: number,
): boolean {
  if (!(appliedCents > 0)) return false;
  return !lots.some((l) => lotRowState(l).checked);
}

/**
 * Automatic mode spends every applicable lot, but only when an apply runs:
 * a lot that became usable after the cart was re-priced (it reached a
 * minimum) sits unticked until something applies again. This is the key of
 * that situation — the applicable lots, by id — or null when there is none
 * to act on. The caller fires ONE coordinated automatic apply per key
 * (credit-topup.ts), so an apply that only changes what is left on the lots
 * never fires another. With no credit on the order, any usable lot is
 * reason enough (all of it fell away when the cart shrank, and it grew back).
 */
export function autoTopUpKey(input: {
  mode: "AUTO" | "MANUAL" | null;
  optedOut: boolean;
  orderEditable: boolean;
  hasCreditApplied: boolean;
  lots: readonly CreditLotView[] | null;
}): string | null {
  if (input.mode !== "AUTO" || input.optedOut || !input.orderEditable) return null;
  if (!input.lots) return null;
  const applicable = input.lots.filter((l) => l.applicable);
  if (applicable.length === 0) return null;
  // Credit on the order: only a usable lot it is not spending is news. None
  // on it (never applied, or all of it fell away when the cart shrank):
  // any usable lot is.
  if (input.hasCreditApplied && !applicable.some((l) => !l.selected)) return null;
  return applicable
    .map((l) => l.id)
    .sort()
    .join(",");
}

/**
 * What to apply right after a gift-card code was redeemed.
 *
 * Automatic mode: apply without lotIds, as always. Otherwise — a manual
 * choice, or a mode not known yet — the lots are read first (`read`), once
 * more if that fails: a manual choice keeps its lots and takes the new card
 * in; a server that answers automatic (or lists no lots at all) is applied
 * automatically. Two failed reads apply NOTHING ("keep"): applying without
 * lotIds would quietly turn the customer's choice back into automatic.
 */
export async function applyAfterRedeem(
  modeBefore: "AUTO" | "MANUAL" | null,
  knownBefore: readonly string[],
  read: () => Promise<{
    selectionMode?: "AUTO" | "MANUAL" | null;
    lots?: CreditLotView[] | null;
  }>,
): Promise<{ kind: "apply"; lotIds: string[] | undefined } | { kind: "keep" }> {
  if (modeBefore === "AUTO") return { kind: "apply", lotIds: undefined };
  let fresh: Awaited<ReturnType<typeof read>> | null = null;
  for (let attempt = 0; attempt < 2 && fresh === null; attempt++) {
    try {
      fresh = await read();
    } catch {
      fresh = null;
    }
  }
  if (fresh === null) return { kind: "keep" };
  if (fresh.selectionMode !== "MANUAL") return { kind: "apply", lotIds: undefined };
  return {
    kind: "apply",
    lotIds: selectionAfterRedeem("MANUAL", knownBefore, fresh.lots ?? []),
  };
}

/**
 * A query's answer from NOW: any read of the same key already in flight
 * (started before a toggle, carrying the old choice) is cancelled rather
 * than joined, and the fetch runs again even if the cache calls its answer
 * fresh. The result lands in the cache under `key`.
 */
export async function freshQuery<T>(
  client: QueryClient,
  key: QueryKey,
  fetcher: () => Promise<T>,
): Promise<T> {
  await client.cancelQueries({ queryKey: key, exact: true });
  return client.fetchQuery({ queryKey: key, queryFn: fetcher, staleTime: 0 });
}

/**
 * Each lot's share of `stepCreditCents` — the credit that will really pay the
 * order as the delivery step stands (`deliveryStepCreditCents`) — so the rows
 * add up to the total under them.
 *
 * The server's per-lot `appliedCents` is what is held for the shipping on the
 * order; after a switch to cheaper shipping it can exceed the order. The rows
 * keep the server's spend order (the list order: soonest-expiring first) and
 * the excess comes off the LAST lots, which is the part released on submit.
 */
export function lotsAppliedOnStep(
  lots: readonly Pick<CreditLotView, "id" | "appliedCents">[],
  stepCreditCents: number,
): Record<string, number> {
  let left = Math.max(0, stepCreditCents);
  const out: Record<string, number> = {};
  for (const lot of lots) {
    const take = Math.min(Math.max(0, lot.appliedCents), left);
    out[lot.id] = take;
    left -= take;
  }
  return out;
}
